<template>
    <div
        id="chat-wrapper"
        @dragenter="dragEnter"
        @dragleave="dragLeave"
        @dragover="dragOver"
        @drop="dropBlock"
        @paste="handleTPaste"
    >
        <FilePicker
            v-if="showSupsisDriveFilePicker"
            :multiple="true"
            @cancel="showSupsisDriveFilePicker = false"
            @confirm="uploadFromSupsisDrive"
        />
        <ImageEditor
            v-if="showImageEditor"
            :files="pendingImageFiles"
            @close="closeImageEditor"
            @save="handleImageSave"
        />
        <FileDragFrame v-if="dragMode" />
        <template v-else>
            <ChatHeader 
                :contact="contact" 
                v-if="showHeader" 
                :is-watching="isWatching()"
                @debugModeChanged="handleDebugModeChanged"
            />
            <template v-if="messageSyncRunning">
                <MessageSkeleton />
            </template>
            <template v-else>
                <ChatMessageList 
                    :contact="contact" 
                    :messages="messages" 
                    :messageSyncRunning="messageSyncRunning"
                    :debugModeEnabled="debugModeEnabled"
                    @reply-comment-action="handleReplyCommentAction"
                />
            </template>
            <ChatMessageArea
                ref="messageArea"
                :contact="contact"
                :messageSyncRunning="messageSyncRunning"
                :visitorTypingMessage="visitorTypingMessage"
                :media-uploading="uploading"
                :messages="messages"
                @message="$emit('message', $event)"
                @media-message="sendMediaMessage"
                @upload-file="openUploadFromSupsis"
            />
        </template>
    </div>
</template>

<script>
import ChatMessageList from "./ChatMessageList.vue";
import ChatHeader from "./ChatHeader.vue";
import { mapActions, mapState } from "vuex";
import ChatMessageArea from "./ChatMessageArea/ChatMessageArea.vue";
import { sendSocketMessage } from "../../../utils/socketStream";
import _ from "lodash";
import axios from "axios";
import mimeTypes from "../../util/mimeTypes";
import { getExtensionFromFileName } from "../../../utils/utils";
import { uploadAsset } from "../../../api/assetUpload";
import FileDragFrame from "./FileDragFrame.vue";
import CONFIG from "../../../config/config";
import { log2 } from "../../../utils/log2";
import MessageSkeleton from "./MessageSkeleton.vue";
import FilePicker from "@/views/Pages/Drive/FilePicker.vue";
import ImageEditor from "./ChatMessageArea/components/ImageEditor.vue";

const log = log2("Chat");
export default {
    name: "Chat",
    components: {
        FilePicker,
        MessageSkeleton,
        FileDragFrame,
        ChatMessageArea,
        ChatHeader,
        ChatMessageList,
        ImageEditor,
    },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        messageSyncRunning: {
            type: Boolean,
            default: false,
        },
        messages: {
            type: Array,
            default: () => [],
        },
        visitorTypingMessage: {
            type: String,
            default: null,
        },
        showHeader: {
            type: Boolean,
            default: true,
        },
        debugModeEnabled: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState({
            agentFileSendEnabled: (state) => _.get(state, "setting.settings.agentFileSendEnabled", true),
            replyToMessages: (state) => state.chat.pinnedReplyToMessages,
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            watchingContactIds: (state) => state.chat.watchingContactIds,
            channels: (state) => state.channel.channels.filter((c) => c.type === "whatsapp-business-api" || c.type === "whatsapp-business-api-supsis"),
        }),
    },
    data: () => ({
        showSupsisDriveFilePicker: false,
        showImageEditor: false,
        pendingImageFiles: [],
        selectedFiles: [],
        dragMode: false,
        uploading: {
            enabled: false,
            percentage: 0,
        },
    }),
    methods: {
        ...mapActions({
            unpinReplyToMessage: "chat/unpinReplyToMessage",
            setLoadingState: "chat/setLoadingState",
            getLoadingState: "chat/getLoadingState",
            getLoadingFilePercent: "chat/getLoadingFilePercent",
        }),
        onSelectedItems(items) {
            this.selectedFiles = items;
        },
        sleep(ms) {
            return new Promise((resolve) => setTimeout(resolve, ms));
        },
        async uploadFromSupsisDrive(selectedFiles) {
            log.info("uploadFromSupsisDrive", selectedFiles);
            this.showSupsisDriveFilePicker = false;
            for (let i = 0; i < selectedFiles.length; i++) {
                this.sendAttachment(selectedFiles[i].asset, this.contact);
            }
        },
        openUploadFromSupsis() {
            this.showSupsisDriveFilePicker = true;

        },
        handleDebugModeChanged(enabled) {
            this.$emit('debugModeChanged', enabled);
        },
        isWatching() {
            return this.watchingContactIds.includes(this.contact._id);
        },
        dragEnter() {
            this.dragMode = true;
        },
        dragLeave() {
            this.dragMode = false;
        },
        async dropBlock(ev) {
            this.dragMode = false;
            ev.preventDefault();
            if (!this.agentFileSendEnabled) {
                return this.$message.error(this.$t("message.dndBlocked"));
            }
            const files = this.$_.get(ev, "dataTransfer.files");
            if (files && files.length > 0) {
                await this.processFiles(Array.from(files));
                ev.stopPropagation();
            }
        },
        dragOver(e) {
            e.preventDefault();
        },
        async uploadFile(file, uploadFile, caption) {

            const conversationId = this.contact?.lastConversation?._id;
            const contact = this.contact;
            let fileType = getExtensionFromFileName(file.name);
            fileType = (fileType || "").toLowerCase();
            let path = `${this.site._id}/file/${this.contact?.lastConversation?._id}`;

            // WABA: Office/PDF + belge/veri/arşiv uzantıları (API uyumsuzsa gönderimde hata); image/video medya
            const validTypes = {
                audio: ["aac", "amr", "mp3", "m4a", "ogg"],
                text: ["txt", "csv", "xml", "tsv", "rtf"],
                application: [
                    "xls",
                    "xlsx",
                    "doc",
                    "docx",
                    "ppt",
                    "pptx",
                    "pdf",
                    "zip",
                    "xml",
                    "json",
                    "rtf",
                    "odt",
                    "ods",
                    "odp",
                    "rar",
                    "7z",
                    "tar",
                    "gz",
                    "bz2",
                    "epub",
                ],
                image: ["jpeg", "png", "jpg", "gif", "webp", "svg"],
                video: ["3gp", "mp4"],
            };

            const validSize = {
                audio: 16 * 1024 * 1024,
                document: 100 * 1024 * 1024,
                image: 5 * 1024 * 1024,
                video: 16 * 1024 * 1024,
            };

            const [type] = file.type.split("/");
            const isBusinessAPI = this.channels.find(
                (channel) => channel._id === this.contact.lastConversation.channel
            );

            const isValidType = (validTypes[type] || []).includes(fileType);
            let maxSize = validSize[type] || CONFIG.MAX_FILE_UPLOAD_SIZE * 1024 * 1024;
            if (isBusinessAPI && (fileType === "svg" || fileType === "gif" || fileType === "webp")) {
                maxSize = validSize.document;
            }
            const isValidSize = file.size <= maxSize;
            if (isBusinessAPI) {
                if (!isValidType) {
                    this.$toasted.error("Geçersiz dosya uzantısı, lütfen geçerli bir dosya yükleyiniz.", {
                        duration: 3000,
                        position: "bottom-center",
                    });
                    return;
                }
                if (!isValidSize) {
                    const text = {
                        audio: "ses",
                        document: "döküman",
                        image: "resim",
                        video: "video",
                        default: "dosya",
                    };
                    this.$toasted.error(
                        `Maksimum ${text[type]} boyutu en fazla ${Math.round(maxSize / 1024 / 1024)} MB olabilir.`,
                        {
                            duration: 3000,
                            position: "bottom-center",
                        }
                    );
                    return;
                }
            }
            let { data } = await uploadAsset({
                path,
                mime: file.type,
                name: file.name,
                ext: mimeTypes[file.type] || fileType || "supsis",
                platform: this.contact.lastConversation.ref,
                size: file.size,
                type: "conversation",
            });
            if (file.size <= CONFIG.MAX_FILE_UPLOAD_SIZE * 1024 * 1024) {
                await axios({
                    method: data.method,
                    url: data.url,
                    data: file,
                    headers: {
                        "Content-Type": file.type,
                    },
                    onUploadProgress: await function (progressEvent) {
                        const percent = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
                        uploadFile.percent = percent;
                        this.setLoadingState(uploadFile);

                        this.getLoadingFilePercent(conversationId).then((percent) => {
                            this.uploading.percentage = percent;
                            this.uploading.enabled = !!percent;
                        });
                    }.bind(this),
                })
                    .then((result) => {
                        this.uploading.enabled = false;
                        uploadFile.finished = true;
                        this.uploading.percentage = 0;
                        this.setLoadingState(uploadFile);
                        this.getLoadingState(uploadFile.fileNumber).then((result) => {
                            this.sendAttachment(data.asset, contact, caption);
                        });
                    })
                    .catch((err) => {
                        this.uploading.enabled = false;
                        uploadFile.failed = true;
                        this.setLoadingState(uploadFile);
                    });
            } else {
                log.warn("input is not suitable for send");
                this.$toasted.error(this.$t("message.assetOversizeMB", { mb: CONFIG.MAX_FILE_UPLOAD_SIZE }), {
                    duration: 3000,
                    position: "bottom-center",
                });
            }
        },
        sendAttachment(asset, contact, caption) {
            contact = contact || this.contact;
            const conversationId = contact?.lastConversation?._id;
            const replyToMessage = Object.values(this.replyToMessages).find(
                (m) => m.raw.visitorId === this.contact._id || m.raw.visitor === this.contact._id
            );

            const isApiMessages = asset.path && asset.path.indexOf("api-messages") !== -1;
            const assetWithUrl = {
                ...asset,
                url: asset.v2Format && asset.cdnUrl
                    ? asset.cdnUrl
                    : asset.cdnUrl || (isApiMessages ? `https://static.supsis.live/${asset.path}` : `https://static.supsis.live/${asset.path}${asset._id}.${asset.ext}`)
            };

            const socketFeedbackMessage = {
                conversationId: conversationId,
                userId: this.user._id,
                username: this.user.name,
                siteId: this.site._id,
                asset: assetWithUrl,
                channelId: _.get(contact, "lastConversation.channel", null) || _.get(contact, "channel", null),
                whatsappNumber: _.get(contact, "lastConversation.whatsappNumber", null) || _.get(contact, "whatsappNumber", null),
                instagramUserId: _.get(contact, "lastConversation.instagramUserId", null) || _.get(contact, "instagramUserId", null),
                messengerUserId: _.get(contact, "lastConversation.messengerUserId", null) || _.get(contact, "messengerUserId", null),
                telegramUserId: _.get(contact, "lastConversation.telegramUserId", null) || _.get(contact, "telegramUserId", null),
                linkedinUserId: _.get(contact, "linkedinUserId", null),
                replyToMessage: _.get(replyToMessage, "raw"),
                visitorId: contact?._id,
                caption: caption,
            };
            sendSocketMessage("user media message", socketFeedbackMessage);
            if (replyToMessage) {
                const conversationId = _.get(
                    replyToMessage,
                    "raw.conversationId",
                    _.get(replyToMessage, "raw.conversation")
                );
                this.unpinReplyToMessage(conversationId);
            }
        },
        async handleTPaste(event) {
            const files = _.get(event, "clipboardData.files");
            if (files && files.length > 0) {
                event.preventDefault();
                if (!this.agentFileSendEnabled) {
                    return this.$message.error(this.$t("message.dndBlocked"));
                }
                await this.processFiles(Array.from(files));
                event.stopPropagation();
                return true;
            }
        },
        sendMediaMessage(data) {
            // Google Drive'dan gelen asset objesi
            if (data && data.asset) {
                this.sendAttachment(data.asset, this.contact);
                return;
            }
            // Normal dosya upload akışı
            this.processFiles([data]);
        },
        handleReplyCommentAction({ commentId, replyToMessage }) {
            log("handleReplyCommentAction", { commentId, replyToMessage });
            
            // Reply to message olarak pin'le
            this.$store.dispatch('chat/pinReplyToMessage', replyToMessage);
        },
        closeImageEditor() {
            this.showImageEditor = false;
            this.pendingImageFiles = [];
        },
        async handleImageSave(images) {
            for (const { file, caption } of images) {
                let uploadFile = {
                    chat_id: this.contact?.lastConversation?._id,
                    fileNumber: Math.random(),
                    finished: false,
                    percent: 0,
                    failed: false,
                };

                if (caption) {
                    const extension = getExtensionFromFileName(file.name);
                    const newFile = new File([file], `${caption}.${extension}`, { type: file.type });
                    await this.uploadFile(newFile, uploadFile, caption);
                } else {
                    await this.uploadFile(file, uploadFile, caption);
                }
            }
        },
        async processFiles(files) {
            const imageFiles = [];
            const otherFiles = [];

            for (const file of files) {
                const fileType = file.type.split("/")[0];
                if (fileType === "image") {
                    imageFiles.push(file);
                } else {
                    otherFiles.push(file);
                }
            }

            if (imageFiles.length > 0) {
                this.pendingImageFiles = imageFiles;
                this.showImageEditor = true;
            }

            for (const file of otherFiles) {
                let uploadFile = {
                    chat_id: this.contact?.lastConversation?._id,
                    fileNumber: Math.random(),
                    finished: false,
                    percent: 0,
                    failed: false,
                };
                await this.uploadFile(file, uploadFile);
            }
        },
    },
};
</script>
<style lang="scss" scoped>
#chat-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: hidden;
}
</style>
